import React from "react";
import DatasourceCardLayout from "../../../shared/cards/DatasourceCardLayout";
import { workbookCache } from "../../../../excel/workbook/cache";
import { useProject } from "../../../context/ProjectContext";
import { useDatasources } from "../../../context/DatasourceContext";
import { useBanner } from "../../../context/BannerContext";
import { EDIPHI_DATASOURCE_META } from "../meta";
import {
  LAST_REFRESHED,
  LAST_REFRESHED_FORMAT,
  EDIPHI_DATASOURCE_DELETE_NOTICE,
  ESTIMATE_REMOVED_DATA_REMAINS,
} from "../../../constants";
import { NO_MILESTONE_NAME } from "./EstimatesList";
import { formatValue, formatCurrency, formatCompactCurrency } from "../format";
import { openEstimateInEdiphi } from "../../../../utils/links";
import { useCardSummary } from "../../../hooks/useCardSummary";
import { useEstimateLoader } from "../../../hooks/useEstimateLoader";
import { clearEstimateSelection } from "../../../../excel/lifecycle";
import { clearEstimateDataTables } from "../../../../excel/reports/dataTables";
import { clearReportWorksheets } from "../../../../excel/reports/api/registry";
import { clearAllChartData } from "../../../../excel/charts/api/registry";
import { reportTypeOfTemplate } from "../../../hooks/useReportCards";
import AccordionLayout, { accordionExpandedCacheKey } from "../../../shared/accordions/AccordionLayout";

interface EstimateDatasourceCardProps {
  onCardClick: () => void;
}

const DETAILS_CACHE_KEY = accordionExpandedCacheKey("estimate");

const formatArea = (area: number) => formatValue(area, 0, "", "gsf");
const formatCostPerSF = (cost: number, area: number) => (area ? `${formatCurrency(cost / area)}/sf` : "-");

const EstimateDatasourceCard = ({ onCardClick }: EstimateDatasourceCardProps) => {
  const { selectedEstimate, setSelectedEstimate } = useProject();
  const { removeDatasource } = useDatasources();
  const { showBanner } = useBanner();
  const { loadEstimate } = useEstimateLoader();
  const summary = useCardSummary("estimate");

  if (!selectedEstimate) return null;

  const milestoneName = summary ? summary.milestoneName || NO_MILESTONE_NAME : "";
  const metricValue = (value: number | undefined) => (value == null ? "—" : formatCurrency(value));

  const handleRemoveCard = async () => {
    removeDatasource("ediphi", "estimate");
    setSelectedEstimate(null);
    void workbookCache.delete(DETAILS_CACHE_KEY);

    // The React state above is already cleared, so a failure here leaves the card gone but the
    // workbook still naming the estimate — say so rather than failing silently.
    try {
      // Estimate reports only: GCGR and contracting data is still loaded, so their sheets keep
      // their numbers. Nothing else reaches this state — refreshAllReports applies the same no-data
      // treatment, but it only ever runs on an estimate *load*.
      await clearReportWorksheets((template) => reportTypeOfTemplate(template) === "estimate");
      await clearEstimateSelection();
      // Estimate-scoped for the same reason as the report filter above. Not folded into
      // clearEstimateSelection, which loadDataTables calls part-way through a load.
      await clearEstimateDataTables();
      // Every chart type is estimate-derived, so clearing the estimate leaves none of them valid.
      await clearAllChartData();
    } catch {
      showBanner(ESTIMATE_REMOVED_DATA_REMAINS, "error");
    }
  };

  const handleEstimateNav = () => {
    openEstimateInEdiphi(selectedEstimate.project, selectedEstimate.id);
  };

  return (
    <DatasourceCardLayout
      header={{
        icon: EDIPHI_DATASOURCE_META.estimate.icon,
        text: EDIPHI_DATASOURCE_META.estimate.label,
      }}
      card={{
        text: summary?.name ?? selectedEstimate.name,
        subtext: summary
          ? [formatCompactCurrency(summary.costOfTradesSubtotal), milestoneName].filter(Boolean).join(" • ")
          : "",
      }}
      onCardClick={onCardClick}
      onLaunch={handleEstimateNav}
      onRefresh={() => void loadEstimate(selectedEstimate)}
      onClose={handleRemoveCard}
      deleteNotice={EDIPHI_DATASOURCE_DELETE_NOTICE}
    >
      <AccordionLayout
        details={[
          { label: "Total Cost", value: metricValue(summary?.costOfTradesSubtotal) },
          ...(milestoneName ? [{ label: "Milestone", value: milestoneName }] : []),
          {
            label: "Area Details",
            value: summary
              ? `${formatArea(summary.grossArea)} • ${formatCostPerSF(summary.costOfTradesSubtotal, summary.grossArea)}`
              : "—",
          },
        ]}
        lastUpdated={summary?.lastRefreshedAt}
        lastUpdatedLabel={LAST_REFRESHED}
        lastUpdatedFormat={LAST_REFRESHED_FORMAT}
        cacheKey={DETAILS_CACHE_KEY}
      />
    </DatasourceCardLayout>
  );
};

export default EstimateDatasourceCard;
